/**
 * @file AppUtility.ts
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */

import { Logger } from "../../logger/Logger";
import { Consent } from "../../services/models/Consent"
import { ConsentType } from "../login/utils/ConsentType";
import { LoginFormView } from "../login/views/LoginFormView";
import useWindowDimensions from "../utils/useWindowDimensions";

/**
 * A class definition for AppUtility
 */
export class AppUtility {

    /**
     * Validates an email
     * @param email string
     * @returns boolean
     */
    public static validateEmail = (email: string) => {
        //eslint-disable-next-line
        if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email)) {
            return (true)
        }
        return (false)
    }

    /**
     * Check consented status
     * @param consents  Consent[]
     * @returns 
     */
    public static isConsented(consents: Consent[], logger: Logger): boolean {
        if (!consents || consents.length <= 0) {
            return false;
        }

        const termsConsent = consents.find(consent => consent.type == ConsentType.Terms);
        const isTermConsented = AppUtility.innerConsentStatus(termsConsent);

        const privacyConsent = consents.find(consent => consent.type == ConsentType.Privacy);
        const isPrivacyConsented = AppUtility.innerConsentStatus(privacyConsent);

        logger.verbose(` User consent status ${isPrivacyConsented} & ${isTermConsented}`, LoginFormView.name);
        return isTermConsented && isPrivacyConsented;
    }

    public static isMobileDeviceScreen() {
        const { width } = useWindowDimensions();
        if (width < 768) {
            return true;
        }
        return false;
    }

    /**
     * Check inner consented status
     * @param consent Consent
     * @returns 
     */
    private static innerConsentStatus(consent: Consent | undefined): boolean {

        if (!consent) {
            return false;
        }

        if (!consent || consent.versions.length <= 0) {
            return false;
        }
        if (!consent.versions[0].consentUsers || consent.versions[0].consentUsers.length <= 0) {
            return false;
        }

        return consent.versions[0].consentUsers[0].active;
    }
}